import React, { useCallback, useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import {
  TenantBilling,
  confirmCardSetup,
  createCardSetup,
  fetchTenantBilling,
} from '../../api/tenantAdmin';
import './TenantBillingPage.css';

/**
 * Where a partner puts a card on file.
 *
 * The card number never reaches us or our logs: PaymentElement is an
 * iframe served by Stripe, and the browser confirms directly against them
 * using a client secret our backend minted for this partner's customer.
 * We only ever learn the brand and last four afterwards.
 */

// Memoised per key rather than read once at module load. Stripe asks that
// loadStripe runs outside render, which the cache satisfies, while reading
// the key when it is needed keeps the module importable without it. Null
// when absent, so a misconfigured build explains itself instead of
// throwing on render.
const stripeByKey = new Map<string, ReturnType<typeof loadStripe>>();

const getStripe = (): ReturnType<typeof loadStripe> | null => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  if (!key) return null;
  if (!stripeByKey.has(key)) stripeByKey.set(key, loadStripe(key));
  return stripeByKey.get(key) as ReturnType<typeof loadStripe>;
};

const CardForm: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !currentUser) return;
    setBusy(true);
    setError('');
    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          // Required even with redirect: 'if_required'. A card whose bank
          // demands 3-D Secure is sent to the issuer as a full page, and
          // Stripe refuses to start that without somewhere to come back
          // to. European business cards ask for it routinely, so omitting
          // this fails for a large share of the partners we have.
          return_url: `${window.location.origin}${window.location.pathname}`,
        },
        // Only redirect when the bank insists. Everything else stays here.
        redirect: 'if_required',
      });
      if (stripeError) {
        setError(stripeError.message || 'That card could not be saved.');
        return;
      }
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setError('That card was not confirmed. Please try again.');
        return;
      }
      // Stripe attaching the card is not the same as it being charged.
      // The backend has to make it the default or every invoice fails.
      await confirmCardSetup(await currentUser.getIdToken(), setupIntent.id);
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'That card could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="tenant-card-form" onSubmit={submit}>
      <PaymentElement />
      {error && <p className="tenant-billing-error" role="alert">{error}</p>}
      <button type="submit" className="tenant-billing-save" disabled={!stripe || busy}>
        {busy ? 'Saving…' : 'Save card'}
      </button>
    </form>
  );
};

const TenantBillingPage: React.FC = () => {
  const { currentUser } = useAuth();
  const stripePromise = getStripe();
  const [billing, setBilling] = useState<TenantBilling | null>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      setBilling(await fetchTenantBilling(await currentUser.getIdToken()));
    } catch (e: any) {
      setError(e?.message || 'Could not load your billing details.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  // Picking up after a 3-D Secure redirect.
  //
  // When the bank takes over the page, this component unmounts mid-flow
  // and the confirm call in CardForm never happens. The card would be
  // attached to the customer and never made the default, which is the
  // exact failure the confirm step exists to prevent, except harder to
  // spot because the partner saw a success screen at their bank.
  //
  // Stripe appends setup_intent to the return_url, so finishing the job
  // here is what closes that hole.
  useEffect(() => {
    const returned = new URLSearchParams(window.location.search).get('setup_intent');
    if (!returned || !currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        await confirmCardSetup(await currentUser.getIdToken(), returned);
        if (cancelled) return;
        setSaved(true);
        load();
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not save that card.');
      } finally {
        // Clear the query so a reload does not try to confirm a spent
        // intent and show an error for something that already worked.
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, load]);

  const startAddingCard = async () => {
    if (!currentUser) return;
    setError('');
    try {
      const { clientSecret: secret } = await createCardSetup(
        await currentUser.getIdToken()
      );
      setClientSecret(secret);
    } catch (e: any) {
      setError(e?.message || 'Could not start adding a card.');
    }
  };

  if (loading) return <div className="tenant-billing"><p>Loading…</p></div>;

  return (
    <div className="tenant-billing">
      <h2>Billing</h2>
      <p className="tenant-billing-intro">
        We charge the card on file automatically each month. You do not need to do
        anything once it is saved.
      </p>

      {error && <div className="tenant-billing-error" role="alert">{error}</div>}
      {saved && (
        <div className="tenant-billing-saved" role="status">
          Card saved. Future invoices will be charged to it automatically.
        </div>
      )}

      {!stripePromise && (
        <p className="tenant-billing-error">
          Card payments are not configured. Please contact us.
        </p>
      )}

      <section className="tenant-billing-current">
        <h3>Payment method</h3>
        {billing?.hasPaymentMethod && billing.card ? (
          <p className="tenant-billing-card">
            <strong>{billing.card.brand?.toUpperCase()}</strong> ending{' '}
            {billing.card.last4}, expires {billing.card.expMonth}/{billing.card.expYear}
          </p>
        ) : (
          // Said plainly rather than left blank: until a card is on file
          // nothing can be collected, and the partner should know that.
          <p className="tenant-billing-none">
            No card on file yet. Invoices cannot be collected until you add one.
          </p>
        )}

        {stripePromise && !clientSecret && (
          <button type="button" className="tenant-billing-save" onClick={startAddingCard}>
            {billing?.hasPaymentMethod ? 'Replace card' : 'Add a card'}
          </button>
        )}
      </section>

      {stripePromise && clientSecret && (
        <section className="tenant-billing-form-wrap">
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CardForm
              onSaved={() => {
                setClientSecret('');
                setSaved(true);
                load();
              }}
            />
          </Elements>
          <p className="tenant-billing-note">
            Your card details go directly to Stripe. We never see or store them.
          </p>
        </section>
      )}

      {billing?.includedPerPeriod ? (
        <section className="tenant-billing-plan">
          <h3>Your plan</h3>
          <p>{billing.includedPerPeriod} NIFs included each month, then per NIF after that.</p>
        </section>
      ) : null}
    </div>
  );
};

export default TenantBillingPage;
